// Klientske zmenšenie fotky pred uploadom.
//
// Prečo: avatar sa nikde nezobrazuje väčší ako ~68 px a server ho aj tak
// prekóduje na 256 px. Posielať surovú fotku z mobilu je zbytočné — a hlavne
// nefunkčné: iPhone prekóduje HEIC → JPEG v plnom 12 Mpx rozlíšení (3–5 MB),
// čo narazí na MAX_CONTENT_LENGTH a upload padne na 413. Zmenšenie v prehliadači
// zároveň vyrieši HEIC — Safari ho dokáže dekódovať do <img>, a von ide JPEG.

/** Zmenší (nikdy nezväčší) rozmery tak, aby sa zmestili do štvorca `max`. */
export function fitWithin(w: number, h: number, max: number): { w: number; h: number } {
  if (!(w > 0) || !(h > 0)) return { w: 0, h: 0 };
  const scale = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}

/** Dekóduje súbor cez <img> — prehliadač sám aplikuje EXIF orientáciu aj HEIC. */
function decode(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

/**
 * Zmenší obrázok na max `maxEdge` px a vráti ho ako JPEG File.
 * Hodí výnimku, keď to prehliadač nezvládne — volajúci vtedy pošle originál.
 * Keď by výsledok bol väčší ako vstup (už malé obrázky), vráti originál.
 */
export async function shrinkImage(
  file: File,
  { maxEdge = 512, quality = 0.85, name = "avatar.jpg" } = {},
): Promise<File> {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("no DOM");
  }
  const img = await decode(file);
  const { w, h } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, maxEdge);
  if (!w || !h) throw new Error("empty image");
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.drawImage(img, 0, 0, w, h);
  const blob: Blob | null = await new Promise((resolve) => {
    if (typeof canvas.toBlob !== "function") { resolve(null); return; }
    canvas.toBlob(resolve, "image/jpeg", quality);
  });
  if (!blob || !blob.size) throw new Error("encode failed");
  if (blob.size >= file.size && file.type === "image/jpeg") return file;  // originál je už menší
  return new File([blob], name, { type: "image/jpeg" });
}
